// @ts-nocheck
/**
 * StructuralValidator.ts — WP-007 Structural Validator
 *
 * Layer 1 of the Template V2 validation pipeline.
 * Checks whether a TemplateDocument (or unknown input) is structurally sound:
 *   - correct kind/version discriminants
 *   - required fields present
 *   - no duplicate IDs
 *   - known component types and valid layout fields
 *
 * Does NOT perform semantic validation (cross-ref resolution). That is Layer 2 (WP-008).
 * Does NOT throw. Returns a ValidationResult.
 * Does NOT mutate its input.
 *
 * Pure TypeScript — no framework dependencies.
 */

import type { ValidationResult } from '../../diagnostics';
import { addIssue, makeFailResult, makePassResult, mergeResults } from '../../diagnostics';
import { validateComponentStructure } from './componentValidator';
import {
  validateElementItemDefaults,
  validatePropertyDefaultValue,
} from './defaultValueValidation';
import { validatePredefinedCanvasItems } from './predefinedCanvasItemsValidation';
import {
  collectGraphWiringDefaultsIds,
  validateElementGraphWiringDefaultsRefs,
  validateElementGraphWiringFields,
  validateGraphWiringDefaultsCatalog,
} from './graphWiringDefaultsValidation';
import {
  collectItemUiActionsIds,
  validateElementItemUiActionsRefs,
  validateItemUiActionsCatalog,
} from './itemUiActionsValidation';
import type { ElementDefinition } from '../../types/element';
import { validateFileTableSchema } from '../../runtime/table/fileTableCsv';
import { validateTableSchema } from '../../runtime/table/tableCodec';


// ── Local helper ─────────────────────────────────────────────────────────────

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** Accept plain string or locale map with at least one non-empty translation. */
function isValidLocalizedTextField(value: unknown): boolean {
  if (typeof value === 'string') {
    return value.trim().length > 0;
  }
  if (!isPlainObject(value)) {
    return false;
  }
  return Object.values(value).some(
    (entry) => typeof entry === 'string' && entry.trim().length > 0,
  );
}

// ── Valid property types ──────────────────────────────────────────────────────

const VALID_PROPERTY_TYPES = new Set([
  'text',
  'multiline',
  'integer',
  'float',
  'boolean',
  'date',
  'datetime',
  'color',
  'url',
  'email',
  'phone',
  'enum',
  'image',
  'json',
  'list',
  'table',
  'fileTable',
]);

// ── Main export ───────────────────────────────────────────────────────────────

/**
 * Validates the structural shape of a TemplateDocument.
 * Returns a ValidationResult. Does not throw.
 * Does not perform semantic checks (cross-ref resolution).
 */
export function validateStructure(document: unknown): ValidationResult {
  // ── Check group 1: Root document shape ──────────────────────────────────

  if (!isPlainObject(document)) {
    return makeFailResult('schema.invalid_root', 'Template root must be a plain object', {
      path: '',
    });
  }

  const doc = document as Record<string, unknown>;

  if (doc.kind !== 'template') {
    return makeFailResult(
      'schema.unknown_kind',
      `Unknown document kind '${String(doc.kind)}'; expected 'template'`,
      { path: 'kind' },
    );
  }

  if (doc.version !== 2) {
    return makeFailResult(
      'schema.invalid_version',
      `Unsupported version '${String(doc.version)}'; expected 2`,
      { path: 'version' },
    );
  }

  // Remaining root checks — continue collecting even on failure
  let result = makePassResult();

  if (!isPlainObject(doc.metadata)) {
    result = addIssue(result, {
      severity: 'error',
      code: 'schema.missing_required_field',
      message: "Field 'metadata' is required and must be a plain object",
      path: 'metadata',
    });
  }

  if (!Array.isArray(doc.elements)) {
    result = addIssue(result, {
      severity: 'error',
      code: 'schema.missing_required_field',
      message: "Field 'elements' is required and must be an array",
      path: 'elements',
    });
  }

  if (!Array.isArray(doc.categories)) {
    result = addIssue(result, {
      severity: 'error',
      code: 'schema.missing_required_field',
      message: "Field 'categories' is required and must be an array",
      path: 'categories',
    });
  }

  // ── Check group 2: Metadata ─────────────────────────────────────────────

  if (isPlainObject(doc.metadata)) {
    result = mergeResults(result, validateMetadata(doc.metadata as Record<string, unknown>));
  }

  const propertyTypeByKey = buildPropertyTypeMap(
    Array.isArray(doc.categories) ? (doc.categories as unknown[]) : [],
  );

  // ── Check group 3: Element definitions ─────────────────────────────────

  if (Array.isArray(doc.elements)) {
    result = mergeResults(
      result,
      validateElements(doc.elements as unknown[], propertyTypeByKey),
    );
  }

  const graphWiringDefaultsIds = collectGraphWiringDefaultsIds(doc.graphWiringDefaults);
  result = validateGraphWiringDefaultsCatalog(doc.graphWiringDefaults, result);
  if (Array.isArray(doc.elements)) {
    result = validateElementGraphWiringDefaultsRefs(
      doc.elements as unknown[],
      graphWiringDefaultsIds,
      result,
    );
    result = validateElementGraphWiringFields(doc.elements as unknown[], result);
  }

  const itemUiActionsIds = collectItemUiActionsIds(
    doc.itemUiActions as readonly { id?: string }[] | undefined,
  );
  result = validateItemUiActionsCatalog(doc.itemUiActions, result);
  if (Array.isArray(doc.elements)) {
    result = validateElementItemUiActionsRefs(
      doc.elements as unknown[],
      itemUiActionsIds,
      result,
    );
  }

  // ── Check group 4: Category bundles ────────────────────────────────────

  if (Array.isArray(doc.categories)) {
    result = mergeResults(result, validateCategories(doc.categories as unknown[]));
  }

  // ── Check group 5: Duplicate ID detection ──────────────────────────────

  result = mergeResults(result, validateDuplicateIds(doc));

  // ── Check group 6: Script definitions (if present) ──────────────────────

  if (isPlainObject(doc.scripts)) {
    const rawScripts = doc.scripts as Record<string, unknown>;
    if (Array.isArray(rawScripts.scripts)) {
      result = mergeResults(result, validateScriptDefinitions(rawScripts.scripts as unknown[]));
    }
  }

  return result;
}

// ── Check group 6: Script definitions ──────────────────────────────────────

function validateScriptDefinitions(scripts: unknown[]): ValidationResult {
  let result = makePassResult();

  for (let i = 0; i < scripts.length; i++) {
    const script = scripts[i];
    const scriptPath = `scripts.scripts[${i}]`;

    if (!isPlainObject(script)) {
      result = addIssue(result, {
        severity: 'error',
        code: 'schema.invalid_field_type',
        message: `Script definition at index ${i} must be a plain object`,
        path: scriptPath,
      });
      continue;
    }

    const s = script as Record<string, unknown>;

    if (typeof s.id !== 'string' || s.id === '') {
      result = addIssue(result, {
        severity: 'error',
        code: 'schema.missing_required_field',
        message: `Script definition at index ${i} is missing required field 'id'`,
        path: `${scriptPath}.id`,
      });
    }

    if (s.language !== 'blockly-js') {
      result = addIssue(result, {
        severity: 'error',
        code: 'schema.invalid_field_type',
        message: `Script definition at index ${i} has unknown language '${String(s.language)}'; expected 'blockly-js'`,
        path: `${scriptPath}.language`,
      });
    } else {
      if (typeof s.code !== 'string' || s.code.trim() === '') {
        result = addIssue(result, {
          severity: 'error',
          code: 'schema.missing_required_field',
          message: `Blockly script definition at index ${i} must have a non-empty 'code' field`,
          path: `${scriptPath}.code`,
        });
      }
    }
  }

  return result;
}

// ── Check group 2: Metadata ─────────────────────────────────────────────────

function validateMetadata(metadata: Record<string, unknown>): ValidationResult {
  let result = makePassResult();

  if (typeof metadata.templateId !== 'string' || metadata.templateId === '') {
    result = addIssue(result, {
      severity: 'error',
      code: 'schema.missing_required_field',
      message: "Field 'metadata.templateId' is required and must be a non-empty string",
      path: 'metadata.templateId',
    });
  }

  if (!isPlainObject(metadata.displayName)) {
    result = addIssue(result, {
      severity: 'error',
      code: 'schema.missing_required_field',
      message: "Field 'metadata.displayName' is required and must be a plain object",
      path: 'metadata.displayName',
    });
  }

  if (typeof metadata.primaryElementId !== 'string' || metadata.primaryElementId === '') {
    result = addIssue(result, {
      severity: 'error',
      code: 'schema.missing_required_field',
      message: "Field 'metadata.primaryElementId' is required and must be a non-empty string",
      path: 'metadata.primaryElementId',
    });
  }

  return result;
}

// ── Check group 3: Element definitions ──────────────────────────────────────

function buildPropertyTypeMap(categories: unknown[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const bundle of categories) {
    if (!isPlainObject(bundle)) {
      continue;
    }
    const properties = bundle.properties;
    if (!Array.isArray(properties)) {
      continue;
    }
    for (const prop of properties) {
      if (!isPlainObject(prop)) {
        continue;
      }
      const categoryId = typeof prop.categoryId === 'string' ? prop.categoryId : '';
      const propertyId = typeof prop.id === 'string' ? prop.id : '';
      const type = typeof prop.type === 'string' ? prop.type : '';
      if (categoryId.length > 0 && propertyId.length > 0 && type.length > 0) {
        map.set(`${categoryId}::${propertyId}`, type);
      }
    }
  }
  return map;
}

function validateElements(
  elements: unknown[],
  propertyTypeByKey: Map<string, string>,
): ValidationResult {
  let result = makePassResult();
  const elementById = new Map<string, ElementDefinition>();

  for (const element of elements) {
    if (isPlainObject(element) && typeof element.id === 'string' && element.id !== '') {
      elementById.set(element.id, element as unknown as ElementDefinition);
    }
  }

  for (let i = 0; i < elements.length; i++) {
    const element = elements[i];
    const elPath = `elements[${i}]`;

    if (!isPlainObject(element)) {
      result = addIssue(result, {
        severity: 'error',
        code: 'schema.invalid_field_type',
        message: `Element at index ${i} must be a plain object`,
        path: elPath,
      });
      continue;
    }

    const el = element as Record<string, unknown>;

    if (typeof el.id !== 'string' || el.id === '') {
      result = addIssue(result, {
        severity: 'error',
        code: 'schema.missing_required_field',
        message: `Element at index ${i} is missing required field 'id'`,
        path: `${elPath}.id`,
      });
    }

    if (!Array.isArray(el.categoryIds)) {
      result = addIssue(result, {
        severity: 'error',
        code: 'schema.missing_required_field',
        message: `Element at index ${i} is missing required field 'categoryIds' (must be an array)`,
        path: `${elPath}.categoryIds`,
      });
    }

    if (!Array.isArray(el.layouts)) {
      result = addIssue(result, {
        severity: 'error',
        code: 'schema.missing_required_field',
        message: `Element at index ${i} is missing required field 'layouts' (must be an array)`,
        path: `${elPath}.layouts`,
      });
    } else {
      result = mergeResults(result, validateLayouts(el.layouts as unknown[], i));
    }

    if (el.itemDefaults !== undefined) {
      result = validateElementItemDefaults(
        el.itemDefaults,
        elPath,
        propertyTypeByKey,
        result,
      );
    }

    if (el.predefinedCanvasItems !== undefined) {
      result = validatePredefinedCanvasItems(
        el as unknown as ElementDefinition,
        i,
        elementById,
        propertyTypeByKey,
        result,
      );
    }
  }

  return result;
}

function validateLayouts(layouts: unknown[], elementIndex: number): ValidationResult {
  let result = makePassResult();
  let placeholderLayoutIndex: number | undefined;

  for (let j = 0; j < layouts.length; j++) {
    const layout = layouts[j];
    const layoutPath = `elements[${elementIndex}].layouts[${j}]`;

    if (!isPlainObject(layout)) {
      result = addIssue(result, {
        severity: 'error',
        code: 'schema.invalid_field_type',
        message: `Layout at ${layoutPath} must be a plain object`,
        path: layoutPath,
      });
      continue;
    }

    const lay = layout as Record<string, unknown>;
    const isPlaceholder = lay.isPlaceholderLayout === true;

    if (typeof lay.id !== 'string' || lay.id === '') {
      result = addIssue(result, {
        severity: 'error',
        code: 'schema.missing_required_field',
        message: `Layout is missing required field 'id'`,
        path: `${layoutPath}.id`,
      });
    }

    if (isPlaceholder) {
      if (placeholderLayoutIndex !== undefined) {
        result = addIssue(result, {
          severity: 'error',
          code: 'layout.placeholder_layout_duplicate',
          message: `Element may have at most one isPlaceholderLayout layout`,
          path: `${layoutPath}.isPlaceholderLayout`,
        });
      } else {
        placeholderLayoutIndex = j;
      }

      if (lay.renderAsBuiltin === true) {
        result = addIssue(result, {
          severity: 'error',
          code: 'layout.placeholder_builtin_conflict',
          message: `isPlaceholderLayout is incompatible with renderAsBuiltin`,
          path: `${layoutPath}.isPlaceholderLayout`,
        });
      }

      if (lay.isDefault === true) {
        result = addIssue(result, {
          severity: 'error',
          code: 'layout.placeholder_is_default_forbidden',
          message: `isPlaceholderLayout layouts must not set isDefault`,
          path: `${layoutPath}.isDefault`,
        });
      }
    }

    if (!Array.isArray(lay.components)) {
      result = addIssue(result, {
        severity: 'error',
        code: 'schema.missing_required_field',
        message: `Layout is missing required field 'components' (must be an array)`,
        path: `${layoutPath}.components`,
      });
    } else {
      const components = lay.components as unknown[];
      if (lay.renderAsBuiltin === true && components.length > 0) {
        result = addIssue(result, {
          severity: 'error',
          code: 'layout.builtin_must_have_empty_components',
          message: `Layout with renderAsBuiltin must have an empty components array`,
          path: `${layoutPath}.components`,
        });
      }
      if (isPlaceholder && components.length === 0) {
        result = addIssue(result, {
          severity: 'error',
          code: 'layout.placeholder_must_have_components',
          message: `isPlaceholderLayout layouts must have at least one component`,
          path: `${layoutPath}.components`,
        });
      }
      for (let k = 0; k < components.length; k++) {
        const compPath = `${layoutPath}.components[${k}]`;
        result = validateComponentStructure(components[k], compPath, result);
      }
    }
  }

  return result;
}

// ── Check group 4: Category bundles ─────────────────────────────────────────

function validateCategories(categories: unknown[]): ValidationResult {
  let result = makePassResult();

  for (let i = 0; i < categories.length; i++) {
    const bundle = categories[i];
    const bundlePath = `categories[${i}]`;

    if (!isPlainObject(bundle)) {
      result = addIssue(result, {
        severity: 'error',
        code: 'schema.invalid_field_type',
        message: `Category bundle at index ${i} must be a plain object`,
        path: bundlePath,
      });
      continue;
    }

    const b = bundle as Record<string, unknown>;

    if (typeof b.bundleId !== 'string' || b.bundleId === '') {
      result = addIssue(result, {
        severity: 'error',
        code: 'schema.missing_required_field',
        message: `Category bundle at index ${i} is missing required field 'bundleId'`,
        path: `${bundlePath}.bundleId`,
      });
    }

    if (!Array.isArray(b.categories)) {
      result = addIssue(result, {
        severity: 'error',
        code: 'schema.missing_required_field',
        message: `Category bundle at index ${i} is missing required field 'categories' (must be an array)`,
        path: `${bundlePath}.categories`,
      });
    }

    if (!Array.isArray(b.properties)) {
      result = addIssue(result, {
        severity: 'error',
        code: 'schema.missing_required_field',
        message: `Category bundle at index ${i} is missing required field 'properties' (must be an array)`,
        path: `${bundlePath}.properties`,
      });
    } else {
      result = mergeResults(result, validateProperties(b.properties as unknown[], i));
    }
  }

  return result;
}

function validateProperties(properties: unknown[], bundleIndex: number): ValidationResult {
  let result = makePassResult();

  for (let j = 0; j < properties.length; j++) {
    const prop = properties[j];
    const propPath = `categories[${bundleIndex}].properties[${j}]`;

    if (!isPlainObject(prop)) {
      result = addIssue(result, {
        severity: 'error',
        code: 'schema.invalid_field_type',
        message: `Property at ${propPath} must be a plain object`,
        path: propPath,
      });
      continue;
    }

    const p = prop as Record<string, unknown>;

    if (typeof p.id !== 'string' || p.id === '') {
      result = addIssue(result, {
        severity: 'error',
        code: 'schema.missing_required_field',
        message: `Property is missing required field 'id'`,
        path: `${propPath}.id`,
      });
    }

    if (typeof p.categoryId !== 'string' || p.categoryId === '') {
      result = addIssue(result, {
        severity: 'error',
        code: 'schema.missing_required_field',
        message: `Property is missing required field 'categoryId'`,
        path: `${propPath}.categoryId`,
      });
    }

    if (!isValidLocalizedTextField(p.name)) {
      result = addIssue(result, {
        severity: 'error',
        code: 'schema.missing_required_field',
        message: `Property is missing required field 'name' (string or localized map)`,
        path: `${propPath}.name`,
      });
    }

    if (typeof p.type !== 'string' || !VALID_PROPERTY_TYPES.has(p.type)) {
      result = addIssue(result, {
        severity: 'error',
        code: 'schema.invalid_field_type',
        message: `Property type '${String(p.type)}' is not a valid PropertyType`,
        path: `${propPath}.type`,
      });
    }

    if ('required' in p && p.required !== undefined) {
      result = addIssue(result, {
        severity: 'warning',
        code: 'schema.deprecated_field',
        message: `Property field 'required' is deprecated and ignored — category properties are never save-blocking`,
        path: `${propPath}.required`,
      });
    }

    if ('aiLookup' in p && p.aiLookup !== undefined && typeof p.aiLookup !== 'boolean') {
      result = addIssue(result, {
        severity: 'error',
        code: 'schema.invalid_field_type',
        message: `Property field 'aiLookup' must be a boolean`,
        path: `${propPath}.aiLookup`,
      });
    }

    if ('defaultValue' in p && p.defaultValue !== undefined && typeof p.type === 'string') {
      result = validatePropertyDefaultValue(
        p.defaultValue,
        p.type,
        `${propPath}.defaultValue`,
        result,
      );
    }

    if (p.type === 'table' || p.type === 'fileTable') {
      const validation = p.validation;
      if (!isPlainObject(validation) || !isPlainObject(validation.tableSchema)) {
        result = addIssue(result, {
          severity: 'error',
          code: 'schema.missing_required_field',
          message:
            p.type === 'fileTable'
              ? `FileTable property requires validation.tableSchema`
              : `Table property requires validation.tableSchema`,
          path: `${propPath}.validation.tableSchema`,
        });
      } else {
        const tableSchema = validation.tableSchema as Record<string, unknown>;
        const columns = Array.isArray(tableSchema.columns)
          ? (tableSchema.columns as never)
          : [];
        const normalizedSchema = {
          columns,
          maxRows: typeof tableSchema.maxRows === 'number' ? tableSchema.maxRows : 0,
          overflow:
            tableSchema.overflow === 'rolling' ? ('rolling' as const) : ('destructive' as const),
        };
        // fileTable: columns only (no CTBL maxRows / overflow clamps).
        // table: full CTBL schema validation including row capacity.
        const schemaError =
          p.type === 'fileTable'
            ? validateFileTableSchema(normalizedSchema)
            : validateTableSchema(normalizedSchema);
        if (schemaError) {
          result = addIssue(result, {
            severity: 'error',
            code: 'schema.invalid_field_type',
            message: schemaError,
            path: `${propPath}.validation.tableSchema`,
          });
        }
      }
    }
  }

  return result;
}

// ── Check group 5: Duplicate ID detection ────────────────────────────────────

function validateDuplicateIds(doc: Record<string, unknown>): ValidationResult {
  let result = makePassResult();

  // Duplicate element IDs
  if (Array.isArray(doc.elements)) {
    const elementIdMap = new Map<string, number>();
    for (let i = 0; i < doc.elements.length; i++) {
      const el = doc.elements[i];
      if (!isPlainObject(el)) continue;
      const elId = (el as Record<string, unknown>).id;
      if (typeof elId !== 'string' || elId === '') continue;
      const prevIndex = elementIdMap.get(elId);
      if (prevIndex !== undefined) {
        result = addIssue(result, {
          severity: 'error',
          code: 'schema.duplicate_id',
          message: `Duplicate element id '${elId}' found at indices ${prevIndex} and ${i}`,
          path: 'elements',
        });
      } else {
        elementIdMap.set(elId, i);
      }
    }

    // Duplicate layout IDs within each element
    for (let i = 0; i < doc.elements.length; i++) {
      const el = doc.elements[i];
      if (!isPlainObject(el)) continue;
      const elem = el as Record<string, unknown>;
      if (!Array.isArray(elem.layouts)) continue;
      const elementId = typeof elem.id === 'string' ? elem.id : `[${i}]`;
      const layoutIdMap = new Map<string, number>();
      for (let j = 0; j < elem.layouts.length; j++) {
        const layout = elem.layouts[j];
        if (!isPlainObject(layout)) continue;
        const layoutId = (layout as Record<string, unknown>).id;
        if (typeof layoutId !== 'string' || layoutId === '') continue;
        const prevIndex = layoutIdMap.get(layoutId);
        if (prevIndex !== undefined) {
          result = addIssue(result, {
            severity: 'error',
            code: 'schema.duplicate_id',
            message: `Duplicate layout id '${layoutId}' within element '${elementId}'`,
            path: `elements[${i}].layouts`,
          });
        } else {
          layoutIdMap.set(layoutId, j);
        }
      }
    }
  }

  // Duplicate category bundle IDs
  if (Array.isArray(doc.categories)) {
    const bundleIdMap = new Map<string, number>();
    for (let i = 0; i < doc.categories.length; i++) {
      const bundle = doc.categories[i];
      if (!isPlainObject(bundle)) continue;
      const bundleId = (bundle as Record<string, unknown>).bundleId;
      if (typeof bundleId !== 'string' || bundleId === '') continue;
      const prevIndex = bundleIdMap.get(bundleId);
      if (prevIndex !== undefined) {
        result = addIssue(result, {
          severity: 'error',
          code: 'schema.duplicate_id',
          message: `Duplicate category bundleId '${bundleId}' found at indices ${prevIndex} and ${i}`,
          path: 'categories',
        });
      } else {
        bundleIdMap.set(bundleId, i);
      }
    }
  }

  return result;
}
